import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getApiBase } from './endpointManager';

/**
 * 續期（refresh token 輪替）的**單一入口**。
 *
 * ## 為什麼需要這支
 *
 * 後端的 refresh token 是「單次有效＋輪替」：用掉一個就換發一個新的，
 * 舊的再被送來即判定為**重用**，並撤銷整組 family（含剛換發的那個）⇒ 使用者被踢回登入頁。
 *
 * 2026-11-02 查 `auth_refresh_tokens` 實際撞到過（9 天 13 次，全同一 UA/IP）：
 *
 *     id=211  issued 13:28:11  replaced_by=212  revoked 13:58:14  reason=rotated
 *     id=212  issued 13:58:14  replaced_by=None revoked 13:58:14  reason=reuse_detected
 *
 * 212 在發出的同一秒就被撤銷 ⇒ 同一個權杖在 13:58:14 被送了兩次。
 * 原因是續期有**兩條互不知道的路**，而 refresh token 放在 httpOnly cookie、
 * 同源的每個分頁共用同一份：
 *
 *   1. `useTokenRefresh` 每 30 分鐘定時續期（且掛載時立刻先續一次）
 *   2. `api.ts` 的 401 攔截器在請求被拒時續期
 *
 * 原本只有第 2 條有鎖，所以「定時 vs 401」「分頁 A vs 分頁 B」都會對撞。
 *
 * ## 三層防護（由近到遠）
 *
 *   1. **同一個 JS context**：共用同一個 in-flight Promise（真正的 single-flight）
 *   2. **跨分頁**：Web Locks API 把同源的續期序列化（不支援時自動略過這層）
 *   3. **冷卻窗**：拿到鎖之後再看「上一次成功續期是多久以前」，夠近就直接視為成功
 *      —— 這層才是真正避免輪替風暴的關鍵，因為序列化只是讓兩次輪替排隊，
 *      並沒有讓第二次變成不必要。
 *
 * 時間戳寫在 `localStorage`（同源跨分頁共享）。隱私模式／關閉儲存時讀寫會丟例外，
 * 全部包 try/catch，退回「只有第 1 層」的行為，不讓續期本身壞掉。
 */

const LAST_OK_KEY = 'authRefreshedAt';
const LOCK_NAME = 'auth-refresh';

/** 定時續期用：排程本身是 30 分鐘一次，25 分鐘內已續過就不必再續。 */
export const SCHEDULED_MIN_INTERVAL_MS = 25 * 60 * 1000;

/** 401 續期用：只擋「同一個過期權杖造成的連鎖 401」，窗口要短，不能掩蓋真的需要續期。 */
export const ON_401_MIN_INTERVAL_MS = 10 * 1000;

let inFlight: Promise<boolean> | null = null;

function readLastOk(): number {
  try {
    const raw = localStorage.getItem(LAST_OK_KEY);
    const n = raw ? Number(raw) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;   // 讀不到就當成「從未續期」，寧可多續一次也不要漏續
  }
}

function writeLastOk(at: number): void {
  try {
    localStorage.setItem(LAST_OK_KEY, String(at));
  } catch {
    /* 無儲存可用 → 少掉冷卻窗這層，其餘照運作 */
  }
}

async function postRefresh(): Promise<boolean> {
  try {
    await axios.post(
      `${getApiBase() || API_BASE_URL}/api/auth/refresh`,
      {},
      { withCredentials: true },
    );
    writeLastOk(Date.now());
    return true;
  } catch {
    return false;
  }
}

/** 有 Web Locks 就序列化跨分頁的續期；沒有就直接執行（退回只靠冷卻窗）。 */
async function withCrossTabLock(fn: () => Promise<boolean>): Promise<boolean> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks?.request) return fn();
  try {
    return await locks.request(LOCK_NAME, fn);
  } catch {
    return fn();   // 鎖本身失敗不能讓續期失敗
  }
}

/**
 * 續期一次。回傳 true 表示「此刻的 cookie 是新的」——
 * 可能是本次續期成功，也可能是別人剛續過（冷卻窗內）。
 */
export function refreshSession(minIntervalMs = 0): Promise<boolean> {
  if (inFlight) return inFlight;      // 第 1 層：同 context 共用

  inFlight = (async () => {
    const elapsedBefore = Date.now() - readLastOk();
    if (minIntervalMs > 0 && elapsedBefore < minIntervalMs) return true;

    return withCrossTabLock(async () => {
      // 第 3 層：拿到鎖後要**重新讀**時間戳 —— 等鎖的這段時間別的分頁可能已經續好了
      const elapsed = Date.now() - readLastOk();
      if (minIntervalMs > 0 && elapsed < minIntervalMs) return true;
      return postRefresh();
    });
  })().finally(() => { inFlight = null; });

  return inFlight;
}

/** 登出時清掉時間戳，避免下次登入的第一次續期被冷卻窗吃掉。 */
export function clearRefreshMark(): void {
  try {
    localStorage.removeItem(LAST_OK_KEY);
  } catch {
    /* 同上，無儲存可用即略過 */
  }
}
